import type { QuickRebuttal, SalesChecklist } from "@expert/contentful";
import type { AudioType } from "@expert/shared-types";

import { sendErrorMetric } from "@expert/cloudwatch-metrics";
import { useNotification } from "@expert/common-ui";
import { Box, Button, Stack, Text } from "@mantine/core";
import { useCallback, useEffect, useEffectEvent, useState } from "react";

import type { EligibleProduct } from "../../../../types";

import { createEnrollment, enrollmentStatusToSaleProgress } from "../../../../api/enrollment";
import { useSalesContext } from "../../../../sales-context/SalesContext";
import { useProductOfferStore } from "../../../../stores";
import { SaleProgress } from "../../../../types";
import { FullOfferChecklist } from "../FullOfferChecklist/FullOfferChecklist";
import { hasDisclosureContent } from "../FullOfferChecklist/utils/disclosureContent";
import { shouldUsePreRecordedDisclosures } from "../FullOfferChecklist/utils/shouldUsePreRecordedDisclosures";
import { LightBulbIcon } from "../Icons";
import { useLanguageStore } from "../language-selector/useLanguageStore";
import { SalesFooter } from "./SalesFooter";
import styles from "./SalesFooter.module.css";

function formatQuickRebuttal(doc?: QuickRebuttal): string {
    if (!doc?.objections?.length) return "";
    const q = (s = "") => `“${s}”`;
    return doc.objections
        .map((objection, i) => {
            const { title, responses = [] } = objection;
            const outerListItem = `${i + 1}. ${title === "Early Rebuttal" ? title : q(title)}`;
            const innerListItems = responses.map((response) => `- ${q(response.text)}`).join("\n");
            return innerListItems ? `${outerListItem}\n${innerListItems}` : outerListItem;
        })
        .join("\n\n");
}

interface FullOfferChecklistStepProps {
    product: EligibleProduct;
    salesChecklist?: SalesChecklist;
    quickRebuttal?: QuickRebuttal;
    sendLinkCopy?: string;
    sentSuccessMessage?: string;
    isGuidedBrowsing?: boolean;
    checklistPlacement?: string;
    requiresChecklistCompletion?: boolean;
    returnLinkToExpert?: boolean;
    sendLinkCommType?: string;
}

export function FullOfferChecklistStep({
    product,
    salesChecklist,
    quickRebuttal,
    sendLinkCopy,
    sentSuccessMessage,
    isGuidedBrowsing,
    checklistPlacement,
    requiresChecklistCompletion,
    returnLinkToExpert,
    sendLinkCommType,
}: FullOfferChecklistStepProps) {
    const {
        logger,
        onQuickRebuttal,
        features,
        sessionId,
        partner,
        apiClients,
        analytics,
        taskData,
        taskType,
        onPlayDisclosure,
        disclosureAudioAvailability,
        isSimulatedVoiceTask,
    } = useSalesContext();
    const { audioType } = useLanguageStore();
    const notification = useNotification();

    // Track which languages have had their disclosure played to completion. Enroll unlocks whenever
    // the *currently selected* language is in this set — so toggling the LanguageSelector away and
    // back to a language already played keeps Enroll enabled, while switching to a not-yet-played
    // language re-locks it. Completion is credited to the language that actually played (captured
    // in-flight by RecordedLegalDisclosuresSection), so a mid-play switch can't unlock Enroll for a
    // language the customer never heard.
    const [completedAudioTypes, setCompletedAudioTypes] = useState<ReadonlySet<AudioType>>(() => new Set());
    // Stable identity so RecordedLegalDisclosuresSection's effect (which lists this in deps)
    // doesn't re-fire and re-`restart` the timer on every parent render.
    const handleDisclosureCompleted = useCallback((playedAudioType: AudioType) => {
        setCompletedAudioTypes((prev) => (prev.has(playedAudioType) ? prev : new Set(prev).add(playedAudioType)));
    }, []);

    // Set when the disclosure audio can't be played (conference failed at play time). We then degrade
    // to the standard Continue button instead of leaving the agent stuck behind an Enroll gated on
    // audio that will never complete.
    const [disclosureAudioFailed, setDisclosureAudioFailed] = useState(false);
    const handleDisclosureAudioFailed = useCallback(() => setDisclosureAudioFailed(true), []);

    // The flag (+ SKU/voice) says we should use recorded disclosures...
    const flagWantsDisclosure = shouldUsePreRecordedDisclosures({
        productSku: product.productSku,
        features,
        taskType,
        isSimulatedVoiceTask,
    });
    // ...but only actually use them when BOTH preconditions hold for the selected language: the
    // partner has a configured disclosure endpoint, AND this SKU has the disclosure content (length +
    // copy). If either is missing — or a play attempt failed at click time — we gracefully degrade to
    // the standard Continue button, otherwise the player can't render / complete and the Enroll gate
    // could never be satisfied, leaving the expert stuck.
    const disclosureContentAvailable = hasDisclosureContent(product.productSku, audioType);
    // Read the per-language boolean once so effects can depend on it directly — depending on the
    // `disclosureAudioAvailability` object (rebuilt every context recompute) would re-fire the
    // degradation metrics repeatedly during a single call.
    const audioAvailableForLanguage = disclosureAudioAvailability[audioType];
    const usePreRecordedDisclosure =
        flagWantsDisclosure && audioAvailableForLanguage && disclosureContentAvailable && !disclosureAudioFailed;

    const requiresDisclosure = usePreRecordedDisclosure && !!onPlayDisclosure;

    // Surface each misconfiguration distinctly (flag on for a voice offer, but the selected language
    // is missing either its audio Quick Connect endpoint or its SKU disclosure content). Reported via
    // effect events so `partner`/`product` stay out of the effect deps and they don't re-fire on
    // every parent render.
    const reportMissingDisclosureAudio = useEffectEvent(() => {
        logger.error(
            { productSku: product.productSku, partner, audioType },
            "Audio disclosures flag is on but no matching audio Quick Connect endpoint is available for the selected language; falling back to Continue",
        );
        void sendErrorMetric("DisclosureAudioEndpointMissing", { partner, audioType });
    });

    const reportMissingDisclosureText = useEffectEvent(() => {
        logger.error(
            { productSku: product.productSku, partner, audioType },
            "Audio disclosure endpoint exists but no disclosure content (length/copy) is configured for the selected language; falling back to Continue",
        );
        void sendErrorMetric("DisclosureTextMissingForLanguage", { partner, audioType });
    });

    // Deliberately NOT keyed on `audioType`: the two derived booleans already change when the
    // selected language's availability/content actually differs. Depending on `audioType` directly
    // would re-fire the metric on every language toggle even when the misconfiguration is unchanged
    // (e.g. a partner with no ARN for either language), inflating alert volume.
    useEffect(() => {
        if (!flagWantsDisclosure) return;
        if (!audioAvailableForLanguage) reportMissingDisclosureAudio();
        else if (!disclosureContentAvailable) reportMissingDisclosureText();
    }, [flagWantsDisclosure, audioAvailableForLanguage, disclosureContentAvailable]);

    // Report the missing-checklist failure via an effect event so `product`/`partner`
    // stay out of the effect deps — otherwise a new `product` reference would re-fire
    // the metric on every parent render while `salesChecklist` is still undefined.
    const reportMissingChecklist = useEffectEvent(() => {
        logger.error({ product, salesChecklist }, "Full offer checklist failed to render");
        void sendErrorMetric("FullOfferChecklistRenderError", { partner });
    });

    useEffect(() => {
        if (!salesChecklist) reportMissingChecklist();
    }, [salesChecklist]);

    const onEnroll = async () => {
        void analytics.dispatcher.dispatchAction("ButtonClick", "Enroll", {
            productSku: product.productSku,
        });

        const enroll = {
            callId: taskData.asurionCallId ?? sessionId,
            sessionId,
            partner,
            productOfferId: product.id,
            productSku: product.productSku,
            salesProvider: product.salesProvider,
        };

        const response = await createEnrollment(apiClients.sales, logger, analytics, enroll);
        const saleProgress = enrollmentStatusToSaleProgress(response.enrollmentStatus);

        if (saleProgress === SaleProgress.Success || saleProgress === SaleProgress.Declined) {
            // SUCCESS and DECLINED are terminal — flip the offer so the existing terminal guards hide
            // the actions. DECLINED (the offer already carries a permanent declination) is an expected
            // outcome, not an error, so no error toast.
            useProductOfferStore.getState().updateProduct(sessionId, product.id, {
                ...product,
                saleProgress,
            });
        } else {
            // FAILURE / ERROR: retryable — leave saleProgress InProgress and surface the error.
            logger.warn(
                {
                    productSku: product.productSku,
                    productOfferId: product.id,
                    sessionId,
                    partner,
                    enrollmentStatus: response.enrollmentStatus,
                },
                "Enrollment did not succeed",
            );
            notification.error("There was an error processing the enrollment");
        }
    };

    const QuickRebuttalButton = () => (
        <Button
            className={styles.quickRebuttalButton}
            variant="outline"
            radius="lg"
            fz="xs"
            size="sm"
            leftSection={<LightBulbIcon />}
            onClick={() => onQuickRebuttal?.(formatQuickRebuttal(quickRebuttal))}
            color="white"
        >
            Quick rebuttal
        </Button>
    );

    return (
        <Stack pos="relative" flex={1}>
            <Box flex={1} style={{ overflowY: "auto" }}>
                {product.saleProgress === SaleProgress.InProgress && salesChecklist ? (
                    <FullOfferChecklist
                        salesChecklist={salesChecklist}
                        productSku={product.productSku}
                        sendLinkCopy={sendLinkCopy}
                        sentSuccessMessage={sentSuccessMessage}
                        onDisclosureCompleted={handleDisclosureCompleted}
                        onDisclosureAudioFailed={handleDisclosureAudioFailed}
                        isGuidedBrowsing={isGuidedBrowsing}
                        checklistPlacement={checklistPlacement}
                        requiresChecklistCompletion={requiresChecklistCompletion}
                        returnLinkToExpert={returnLinkToExpert}
                        sendLinkCommType={sendLinkCommType}
                    />
                ) : (
                    <Text mt="20%" c="dark.1" size="sm">
                        Offer checklist is currently unavailable. Try again later
                    </Text>
                )}
            </Box>
            <SalesFooter
                product={product}
                buttonSlot={quickRebuttal && <QuickRebuttalButton />}
                isGuidedBrowsing={isGuidedBrowsing}
                continueButtonProps={
                    usePreRecordedDisclosure
                        ? {
                              text: "Enroll",
                              onClick: onEnroll,
                              // Terminal offers (SUCCESS/DECLINED) must not allow another Enroll: the
                              // step isn't unmounted on flip (IntegratedFlow routes off `step`, and the
                              // footer renders outside the saleProgress guard above), so gate here too.
                              isDisabled:
                                  product.saleProgress !== SaleProgress.InProgress ||
                                  (requiresDisclosure && !completedAudioTypes.has(audioType)),
                          }
                        : undefined
                }
            />
        </Stack>
    );
}
